"use client";

import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useState } from "react";

interface FAQItemProps {
	question: string;
	answer: string;
	isOpen: boolean;
	onToggle: () => void;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		onToggle();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			onToggle();
		}
	};

	return (
		<div className="w-full">
			<button
				type="button"
				className={`w-full bg-[rgba(231,236,235,0.08)] shadow-[0px_2px_4px_rgba(0,0,0,0.16)] overflow-hidden rounded-[10px] outline-1 outline-border outline-offset-[-1px] transition-all duration-500 ease-out cursor-pointer text-left`}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				aria-expanded={isOpen}
				aria-controls={`faq-answer-${question}`}
			>
				<div className="w-full px-5 py-[18px] pr-4 flex justify-between items-center gap-5 text-left transition-all duration-300 ease-out">
					<div className="flex-1 text-foreground text-base font-medium leading-6 break-words">
						{question}
					</div>
					<div className="flex justify-center items-center">
						<ChevronDown
							className={`w-6 h-6 text-muted-foreground transition-all duration-500 ease-out ${isOpen ? "rotate-180 scale-110" : "rotate-0 scale-100"}`}
						/>
					</div>
				</div>
			</button>
			<div
				id={`faq-answer-${question}`}
				className={`overflow-hidden transition-all duration-500 ease-out ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
				style={{
					transitionProperty: "max-height, opacity, padding",
					transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
				}}
			>
				<div
					className={`px-5 transition-all duration-500 ease-out ${isOpen ? "pb-[18px] pt-2 translate-y-0" : "pb-0 pt-0 -translate-y-2"}`}
				>
					<div className="text-foreground/80 text-sm font-normal leading-6 break-words">
						{answer}
					</div>
				</div>
			</div>
		</div>
	);
};

export function FAQSection() {
	const [openItems, setOpenItems] = useState<Set<number>>(new Set());
	const t = useTranslations("FAQ");

	const toggleItem = (index: number) => {
		const newOpenItems = new Set(openItems);
		if (newOpenItems.has(index)) {
			newOpenItems.delete(index);
		} else {
			newOpenItems.add(index);
		}
		setOpenItems(newOpenItems);
	};

	const faqData = [
		{
			question: t("items.whatIsBrik.question"),
			answer: t("items.whatIsBrik.answer"),
		},
		{
			question: t("items.howItWorks.question"),
			answer: t("items.howItWorks.answer"),
		},
		{
			question: t("items.integrations.question"),
			answer: t("items.integrations.answer"),
		},
		{
			question: t("items.freePlan.question"),
			answer: t("items.freePlan.answer"),
		},
		{
			question: t("items.features.question"),
			answer: t("items.features.answer"),
		},
		{
			question: t("items.security.question"),
			answer: t("items.security.answer"),
		},
	];

	return (
		<section className="w-full pb-20 md:pb-40 px-5 relative flex flex-col justify-center items-center">
			<div className="w-[300px] h-[500px] absolute top-[150px] left-1/2 -translate-x-1/2 origin-top-left rotate-[-33.39deg] bg-primary/10 blur-[100px] z-0" />
			<div className="self-stretch pt-8 pb-8 md:pt-14 md:pb-14 flex flex-col justify-center items-center gap-2 relative z-10">
				<div className="flex flex-col justify-start items-center gap-4">
					<h2 className="w-full text-center text-foreground text-4xl font-semibold leading-10 break-words">
						{t("title")}
					</h2>
					<p className="self-stretch text-center text-muted-foreground text-sm font-medium leading-[18.20px] break-words">
						{t("description")}
					</p>
				</div>
			</div>
			<div className="w-full max-w-[600px] pt-0.5 pb-10 flex flex-col justify-start items-start gap-4 relative z-10">
				{faqData.map((faq, index) => (
					<FAQItem
						key={faq.question}
						{...faq}
						isOpen={openItems.has(index)}
						onToggle={() => toggleItem(index)}
					/>
				))}
			</div>
		</section>
	);
}
